// src/components/finance/AlertsPanel.tsx

import React from 'react';
import {
  AlertTriangle, 
  Clock, 
  XCircle,
  Calendar,
  Package,
  User,
  Clock3
} from 'lucide-react';
import { PackageAlert } from '../../services/AlertService';

interface AlertsPanelProps {
  alerts: PackageAlert[];
  onPatientClick?: (patientId: string) => void;
  onExtendPackage?: (alert: PackageAlert) => void;
  extendDisabled?: boolean;
}

export default function AlertsPanel({ alerts, onPatientClick, onExtendPackage, extendDisabled }: AlertsPanelProps) {
  if (alerts.length === 0) {
    return (
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-semibold">Alertas de Vencimento</h3>
        </div>
        <div className="text-center py-8 text-gray-500">
          <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Nenhum alerta de vencimento no momento</p>
          <p className="text-sm">Todos os pacotes estão dentro do prazo</p>
        </div>
      </div>
    );
  }

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'expired':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'critical':
        return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-600" />;
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'expired':
        return 'border-red-200 bg-red-50';
      case 'critical':
        return 'border-orange-200 bg-orange-50';
      default:
        return 'border-yellow-200 bg-yellow-50';
    }
  };

  const getAlertTextColor = (type: string) => {
    switch (type) {
      case 'expired':
        return 'text-red-800';
      case 'critical':
        return 'text-orange-800';
      default:
        return 'text-yellow-800';
    }
  };

  return (
    <div className="bg-white rounded-xl border shadow-sm">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            <h3 className="text-lg font-semibold">Alertas de Vencimento</h3>
          </div>
          <div className="text-sm text-gray-500">
            {alerts.length} alerta{alerts.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      <div className="divide-y max-h-96 overflow-y-auto">
        {alerts.map((alert, index) => (
          <div 
            key={`${alert.patientId}-${index}`}
            className={`p-4 border-l-4 ${getAlertColor(alert.alertType)} ${
              onPatientClick ? 'cursor-pointer hover:bg-opacity-75' : ''
            }`}
            onClick={() => onPatientClick?.(alert.patientId)}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                {getAlertIcon(alert.alertType)}
                <div className="flex-1">
                  <div className={`font-medium ${getAlertTextColor(alert.alertType)}`}>
                    {alert.patientName}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {alert.packageName}
                  </div>
                  
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {alert.alertType === 'expired' 
                        ? `Expirou há ${Math.abs(alert.daysUntilExpiry)} dias`
                        : `${alert.daysUntilExpiry} dias restantes`
                      }
                    </div>
                    <div className="flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      Restam {alert.sessionsRemaining} de {alert.totalSessions} sessões
                    </div>
                    {alert.sessionsUsed !== undefined && (
                      <div className="flex items-center gap-1 text-emerald-600">
                        <User className="w-3 h-3" />
                        {alert.sessionsUsed} sessão{alert.sessionsUsed === 1 ? '' : 's'} realizadas
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-end gap-2">
                <div className={`text-xs px-2 py-1 rounded-full font-medium ${
                  alert.alertType === 'expired' 
                    ? 'bg-red-100 text-red-800'
                    : alert.alertType === 'critical'
                    ? 'bg-orange-100 text-orange-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {alert.alertType === 'expired' 
                    ? 'EXPIRADO'
                    : alert.alertType === 'critical'
                    ? 'CRÍTICO'
                    : 'ATENÇÃO'
                  }
                </div>
                {onExtendPackage && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      onExtendPackage(alert);
                    }}
                    disabled={extendDisabled}
                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded border ${
                      extendDisabled
                        ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'text-blue-600 border-blue-200 hover:bg-blue-50'
                    }`}
                    title="Estender prazo do pacote"
                  >
                    <Clock3 className="w-3 h-3" />
                    Estender prazo
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
